import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import Cookies from "js-cookie";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import { useRouter } from "next/router";
import { motion } from "framer-motion";
import Image from "next/image";
//styles
import styles from "./handle.module.scss";
//component
import Input from "@/components/common/input/input";
import IconButton from "@/components/common/iconButton/iconButton";
import Loading from "@/components/common/loading/loading";
import PrimaryButton from "@/components/common/primaryButton/primaryButton";
//types
import { ButtonType } from "@/components/common/primaryButton/types";
import { AnimationType } from "../../loginmodal";
import { HandlePropType } from "./types";
import { LoginStep } from "../../types";
//api
import { ModifyUser, ModifyUserBody } from "@/apis/modifyUser/modifyUser";
import {
  CheckUserName,
  CheckUserNameBody,
  CheckUserNameResponse,
} from "@/apis/checkUserName/checkUserName";
import { getPresetName, getPresetNameBody } from "@/apis/getPresetName";
import { fetchProfanityCheck } from "@/apis/fetchProfanityCheck";
//utils
import { checkSpace } from "@/helper/space";
import { checkSpecialCharacter } from "@/helper/specialCharacter";
import {
  mixpanelTrack,
  setSuperMixpanelProperty,
} from "@/helper/mixpanelService";
//context
import { useAppStore } from "@/providers/app";
//zustand
import { useLoginStore } from "@/providers/login/login.provider";
import { useIsMobile } from "@/helper/useIsMobile";
import { URLS } from "@/utils/baseUrl";
import { pushManualGTMEvent } from "@/utils/helpers/gtm";

const enum loadingType {
  LOADING,
  CHECKLOADING,
  VALID,
  INVALID,
  NONE,
}

const Handle = ({ close }: HandlePropType) => {
  const isKeyboardOpen = useDetectKeyboardOpen();
  const isMobile = useIsMobile();

  const router = useRouter();

  const gameData = useAppStore((state) => state.gameData);
  const getUserData = useAppStore((state) => state.getUserData);

  const setState = useLoginStore((state) => state.setState);
  const token = useLoginStore((state) => state.token);
  const id = useLoginStore((state) => state.id);
  const animation = useLoginStore((state) => state.animationType);
  const name = useLoginStore((state) => state.name);
  const email = useLoginStore((state) => state.email);
  const phone = useLoginStore((state) => state.phone);

  const [handle, setHandle] = useState<string>("");
  const [loading, setLoading] = useState<loadingType>(loadingType.NONE);
  const [error, setError] = useState<string>("");
  const [profanity, setProfanity] = useState<string[]>([]);
  const showBackButton = useLoginStore((state) => state.showBackButton);

  const presetName = useCallback(async () => {
    try {
      let body: getPresetNameBody = {
        firstName: name ? name : "",
      };
      const response = await getPresetName(token, body);
      setHandle(response);
    } catch (e) {
      console.error("presetName error", e);
    }
  }, [name, token]);

  const getProfanity = useCallback(async () => {
    try {
      const response = await fetchProfanityCheck(token);
      setProfanity(response);
    } catch (e) {
      console.error("getProfanity error", e);
    }
  }, [token]);

  useEffect(() => {
    presetName();
    getProfanity();
  }, [getProfanity, presetName]);

  const changeHandle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > e.target.maxLength) {
      setHandle(e.target.value.slice(0, e.target.maxLength).toLowerCase());
    } else {
      setHandle(e.target.value.toLowerCase());
    }
    setError("");
    setLoading(loadingType.NONE);
  }, []);

  const checkAvailability = useCallback(async () => {
    if (id) {
      try {
        setLoading(loadingType.CHECKLOADING);
        let body: CheckUserNameBody = {
          handle: handle,
          id: id,
        };
        const response: CheckUserNameResponse = await CheckUserName(
          token,
          body
        );

        if (response?.data?.data?.length > 0) {
          setLoading(loadingType.INVALID);
        } else {
          setLoading(loadingType.VALID);
        }
        //setLoading(loadingType.NONE)
      } catch (e) {
        setLoading(loadingType.NONE);
        console.error("check user name error", e);
      }
    }
  }, [handle, id, token]);

  const checkErrors = useCallback(async () => {
    if (handle !== "") {
      if (handle.length < 3) {
        setError("Please enter minimum of 3 charcters");
      } else if (checkSpace(handle)) {
        setError("You can’t have spaces in your name. Please try again.");
      } else if (checkSpecialCharacter(handle)) {
        setError(
          "Special characters are not allowed. Lets give it another go."
        );
      } else if (
        profanity.some((badWord) =>
          handle.toLocaleLowerCase().includes(badWord)
        )
      ) {
        setError(
          "We do not allow usernames that are offensive or profane. Kindly pick one that isn’t."
        );
      } else {
        setError("");
        checkAvailability();
      }
    }
  }, [checkAvailability, handle, profanity]);

  const handleClose = useCallback(() => {
    setState({
      animationType: AnimationType.SLIDEDOWN,
      step: LoginStep.PHONE,
    });
    close && close();
  }, []);

  const updateHandle = useCallback(async () => {
    try {
      // const id = Cookies.get("_ID");
      const body: ModifyUserBody = {
        handle: handle.trim().toLocaleLowerCase(),
      };
      if (id) {
        const response = await ModifyUser(token, id, body);
        if (response?._id) {
          Cookies.set("ACCESS_TOKEN", token);
          Cookies.set("_ID", id);
          getUserData(token);
          pushManualGTMEvent("signup_status", "success", {
            id: response?._id,
          });
          mixpanelTrack("Signup - User Created", {
            phone,
            name,
            email,
            handle: handle.trim().toLocaleLowerCase(),
            id: response?._id,
          });
        }
        mixpanelTrack("Signup - Handle Entered", {
          phone,
          name,
          email,
          handle: handle.trim().toLocaleLowerCase(),
        });
        setSuperMixpanelProperty(response?.phone, "Handle", response?.handle);
      }
      setLoading(loadingType.NONE);
      handleClose();
      const status = Cookies.get("PayJoinLogin");
      if (gameData && status) {
        router.push(`${URLS.GAME_CART}/${gameData?._id}`);
      }
    } catch (e) {
      setLoading(loadingType.NONE);
      console.error("error ModifyUser", e);
    }
  }, [handle, token, id, gameData, phone, name, email]);

  useEffect(() => {
    checkErrors();
  }, [handle, checkErrors]);

  const renderIcon = useCallback(() => {
    switch (loading) {
      case loadingType.CHECKLOADING:
        return <Loading />;
      case loadingType.VALID:
        return (
          <Image
            className={styles.icon}
            height={20}
            width={20}
            src={"/images/Subtract.png"}
            alt={"valid"}
          />
        );
      default:
        return <></>;
    }
  }, [loading]);

  const handleBack = useCallback(() => {
    setState({
      animationType: AnimationType.SLIDEINLEFT,
      step: !isMobile ? LoginStep.NAME_EMAIL : LoginStep.NAME,
    });
  }, [isMobile]);

  return (
    <motion.div
      className={styles["container"]}
      initial={{
        x: animation === AnimationType.SLIDEINRIGHT ? "100%" : "-100%",
      }}
      animate={{ x: "0%" }}
      transition={{ type: "spring", damping: 20, stiffness: 100 }}
      exit={{ x: "-100%" }}
    >
      <div className={styles["header"]}>
        <>
          {showBackButton && (
            <Image
              onClick={handleBack}
              className={styles["arrow"]}
              height={22}
              width={22}
              src={"/icons/arrow-left.svg"}
              alt={"close-icon"}
            />
          )}
        </>
        <p>Create your Turf ID</p>
        <div />
      </div>

      <div className={classNames(styles["title-cont"])}>
        <div className={classNames(styles["mobile"])}>
          <p>Pick a handle</p>
          <span>How would you like to be known on court?</span>
        </div>
        <div className={classNames(styles["web"])}>
          <p>Pick a handle for your account</p>
        </div>
      </div>
      <div className={classNames(styles["ph-24"], styles["relative"])}>
        <Input
          value={handle}
          maxLength={15}
          placeholder="@"
          //valid
          onChange={changeHandle}
          rightIcon={renderIcon()}
        />
        <div className={classNames(styles["mt-16"], styles["relative"])}>
          <p className={classNames(styles.errorText)}>{error || ""}</p>
          {loading === loadingType.INVALID && (
            <p className={classNames(styles.errorText)}>
              This username already exists. Please pick another
            </p>
          )}
        </div>
      </div>

      <div
        className={classNames(
          styles["button_container"],
          isKeyboardOpen && styles["bottom-45"]
        )}
      >
        <p className={classNames(styles["terms_text"], styles["length-text"])}>
          {handle.length}/15 Characters
        </p>
        <div className={styles["circular-button"]}>
          <IconButton
            loading={loading === loadingType.LOADING}
            disabled={
              loading === loadingType.LOADING ||
              loading === loadingType.INVALID ||
              error !== ""
            }
            onClick={updateHandle}
          />
        </div>
        <div className={styles["full-button"]}>
          <PrimaryButton
            buttonType={
              loading === loadingType.LOADING ||
              loading === loadingType.INVALID ||
              error !== ""
                ? ButtonType.DISABLED
                : ButtonType.PRIMARY
            }
            text={"Continue"}
            onPress={updateHandle}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default Handle;
